/** @jsxRuntime automatic */
import type { ImgHTMLAttributes } from 'react'
import { useImagesCdnBase } from '../cms/images-cdn-context'
import { MAX_IMAGE_WIDTH, generateSrcset, getImageUrl, mediaKeyOf } from '../cms/image-url'

export interface ImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'loading'> {
  src: string
  alt: string
  width?: number | string
  height?: number | string
  /**
   * Above-the-fold / LCP image: eager-load with high fetch priority. Default
   * false → lazy-loaded with async decode. ⚠️ A hero MUST pass this: swapping an
   * `<img>` (eager by default) for `<Image>` without it makes the most important
   * image on the page load LAZILY.
   */
  priority?: boolean
  /** Rendered width for the browser's srcset choice. Default `100vw` — it can over-fetch, never under. */
  sizes?: string
  /** Cap the srcset (px). Default: every breakpoint up to MAX_IMAGE_WIDTH. */
  maxWidth?: number
  /** Override the payload's CDN base (tests, or a site with no SiteContentProvider). */
  cdnBase?: string
}

/**
 * `<img>` with the loading defaults the review found missing, and — for CMS
 * media only — a responsive srcset from the image CDN.
 *
 * ⚠️ NO WRAPPER, NO FADE, ON PURPOSE. `CmsImage` renders its image at
 * `opacity-0` until a React onLoad, which on a prerendered page ships every
 * image invisible until hydration, and it moves `className` onto a wrapper div,
 * which breaks art-directed images (Sam's hero is positioned by `--hero-pan` on
 * the img's own class). This renders one `<img>` with the attributes it was
 * given, so swapping it in for `<img>` changes nothing but src/srcset/sizes/
 * loading/decoding/fetchpriority.
 *
 * Resizes ONLY what `mediaKeyOf` recognises: an absolute url on the payload's CDN
 * or a bare `orgs/` key. A site's own `/images/...` asset is rendered untouched —
 * sending it through the resolver would rewrite it onto the CDN as a broken image.
 *
 * Measured 2026-09-11 before relying on the CDN: no srcset candidate is heavier
 * than its original (a width outside the allowed list returns the original), and
 * CloudFront caches `f=auto` per Accept, so no browser is sent a format it
 * cannot decode.
 */
export function Image({
  src,
  alt,
  width,
  height,
  priority = false,
  decoding,
  sizes,
  maxWidth,
  cdnBase,
  ...rest
}: ImageProps) {
  const contextBase = useImagesCdnBase()
  const base = cdnBase ?? contextBase
  const key = mediaKeyOf(src, base)
  const media = key
    ? {
        src: getImageUrl(key, base, { width: Math.min(maxWidth ?? MAX_IMAGE_WIDTH, MAX_IMAGE_WIDTH), format: 'auto' }),
        srcSet: generateSrcset(key, base, maxWidth),
        sizes: sizes ?? '100vw',
      }
    : { src }
  return (
    <img
      {...media}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding={decoding ?? 'async'}
      // Lowercase, not React 19's camelCase `fetchPriority`: this package
      // supports react ^18 || ^19, and React 18 warns on the camelCase prop for
      // every image it renders. The lowercase form is the real HTML attribute
      // and both majors pass it through verbatim.
      {...(priority ? ({ fetchpriority: 'high' } as Record<string, string>) : {})}
      {...rest}
    />
  )
}
